import type { FetchLike } from 'ProdantixSdk/core/http';
import { PushClient } from 'ProdantixSdk/core/push';

export type { PushDevice, PushPermission, PushPlatform } from 'ProdantixSdk/core/push';
export { PushClient } from 'ProdantixSdk/core/push';

/** The service worker file a site serves from its own origin, copied from
 *  this package's `dist/prodantix-push-sw.js`. */
export const DEFAULT_SERVICE_WORKER = '/prodantix-push-sw.js';

export type PushRegistration = { deviceId: string; status: 'registered' } | { status: 'denied' } | { status: 'unsupported' };

interface SubscriptionLike {
  toJSON(): unknown;
}

interface PushManagerLike {
  getSubscription(): Promise<SubscriptionLike | null>;
  subscribe(options: { applicationServerKey: Uint8Array; userVisibleOnly: boolean }): Promise<SubscriptionLike>;
}

interface WorkerRegistrationLike {
  pushManager: PushManagerLike;
}

export interface PushBrowser {
  register(url: string): Promise<WorkerRegistrationLike>;
  requestPermission(): Promise<'default' | 'denied' | 'granted'>;
}

export interface RegisterPushOptions {
  /** The project's public key, the one the SDK client is built with. */
  apiKey: string;
  /** The push app, from Settings > Push in the console. */
  appId: string;
  /** The test seam for the Notification and service worker APIs; the page's
   *  own when left out. */
  browser?: PushBrowser;
  distinctId: string;
  fetch?: FetchLike;
  /** The edge host the SDK client reads flags from. */
  host: string;
  serviceWorker?: string;
}

/** The Notification and service worker APIs of the page, none where the
 *  browser lacks either. */
export const pageBrowser = (): PushBrowser | null => {
  const scope = globalThis as {
    Notification?: { requestPermission(): Promise<'default' | 'denied' | 'granted'> };
    PushManager?: unknown;
    navigator?: { serviceWorker?: { ready: Promise<WorkerRegistrationLike>; register(url: string): Promise<unknown> } };
  };
  const workers = scope.navigator?.serviceWorker;
  const notification = scope.Notification;
  if (workers === undefined || notification === undefined || scope.PushManager === undefined) return null;
  return {
    register: async url => {
      await workers.register(url);
      return workers.ready;
    },
    requestPermission: () => notification.requestPermission(),
  };
};

/** A VAPID key in base64url as the bytes `applicationServerKey` takes. */
export const keyBytes = (base64url: string): Uint8Array => {
  const padded = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const binary = atob(padded);
  return Uint8Array.from(binary, char => char.charCodeAt(0));
};

/** The worker's address, carrying the edge host and project key it reports
 *  opened notifications with. */
export const workerUrl = (path: string, host: string, apiKey: string): string => `${path}${path.includes('?') ? '&' : '?'}host=${encodeURIComponent(host)}&key=${encodeURIComponent(apiKey)}`;

/** Asks the person to allow notifications, subscribes the browser to the
 *  app's Web Push and registers the subscription for them. Call it from a
 *  click: browsers refuse a permission prompt the person did not start. */
export const registerPush = async (options: RegisterPushOptions): Promise<PushRegistration> => {
  const browser = options.browser ?? pageBrowser();
  if (browser === null) return { status: 'unsupported' };
  const permission = await browser.requestPermission();
  if (permission !== 'granted') return { status: 'denied' };
  const client = new PushClient({ fetch: options.fetch, host: options.host, projectKey: options.apiKey });
  const registration = await browser.register(workerUrl(options.serviceWorker ?? DEFAULT_SERVICE_WORKER, options.host, options.apiKey));
  const subscription = (await registration.pushManager.getSubscription()) ?? (await registration.pushManager.subscribe({ applicationServerKey: keyBytes(await client.vapidKey(options.appId)), userVisibleOnly: true }));
  const deviceId = await client.register({ appId: options.appId, distinctId: options.distinctId, permission: 'granted', platform: 'web', token: JSON.stringify(subscription.toJSON()) });
  return { deviceId, status: 'registered' };
};
